import { useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, generatePath } from 'react-router-dom';

import { CategoryListItemExt, Poll, PostListItem, PostSubTypes, Tag } from 'api';
import { Links } from 'settings';
import { InstanceType } from 'types';
import { getFullName } from 'utils';

interface UsePostItemProps {
  author: PostListItem['author'];
  meta?: PostListItem['meta'];
  tags?: string[];
  category_tags?: Tag[];
  id: string;
  isPreview: boolean;
  description: string;
  type: string;
  published_at?: string;
  updated_at?: string;
  title: string;
  categoriesList: CategoryListItemExt[];
  poll?: Poll;
}

export function usePostItem({
  author,
  meta,
  tags,
  category_tags,
  id,
  isPreview,
  description,
  type,
  title,
  categoriesList,
  poll,
}: UsePostItemProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWelcomePost = meta?.sub_type === PostSubTypes.WELCOME;
  const isBirthdayPost = meta?.sub_type === PostSubTypes.BIRTHDAY;
  const isPollPost = (poll?.options.length ?? 0) > 0;
  const isKPICompletedPost = meta?.sub_type === PostSubTypes.KPI_TARGET;
  const birthDate = meta?.birth_date;
  const pollId = meta?.poll_id;
  const postRef = useRef<HTMLDivElement>(null);

  const offset = searchParams.get('offset');
  const searchPostId = searchParams.get('postId');

  const textWithNewLines = (description || '').replace(/\n/g, '<br />');
  const isDocumentPost = type === InstanceType.document && !isPreview;

  const authorName = getFullName({
    firstName: author.first_name,
    lastName: author.last_name,
  });

  const TITLE = title;
  const categoryTags = category_tags?.map((tag) => {
    const category = categoriesList.find((cat) => cat.id === tag.id);
    if (category) {
      return category;
    } else {
      return { ...tag, icon: 'untagged' };
    }
  });
  const otherTagsCount = tags && tags.length - 1;

  function goToPostDetails() {
    navigate(
      generatePath(Links.comment.index, {
        postId: id,
      }),
      { state: offset ? { offset: offset.replace(/\D/g, '') } : {} },
    );
  }

  useEffect(() => {
    if (postRef.current && searchPostId === id) {
      postRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [id, searchPostId]);

  const handlePostClick = isPreview ? undefined : goToPostDetails;

  const onNavigateToNewUser = () => {
    navigate(
      generatePath(Links.we.employees.employee.index, {
        employeeId: author.id,
      }),
    );
  };

  return {
    authorName,
    postRef,
    categoryTags,
    otherTagsCount,
    textWithNewLines,
    postTitle: TITLE,
    birthDate,
    isDocumentPost,
    isWelcomePost,
    isBirthdayPost,
    isPollPost,
    isKPICompletedPost,
    pollId,
    goToPostDetails,
    handlePostClick,
    onNavigateToNewUser,
  };
}
